import React from 'react';

import loadable from '@loadable/component';

/**
 * SSR-safe loading placeholder for video players
 * Displays during server-side rendering and while the player is loading
 */
const VideoPlaceholder: React.FC<{ style?: React.CSSProperties }> = ({ style }) => (
  <div
    style={{
      width: '100%',
      height: '100%',
      backgroundColor: '#000',
      ...style,
    }}
  />
);

/**
 * SSR-safe ReactPlayer (lazy-loaded)
 *
 * Automatically loads the appropriate player based on URL.
 * Uses react-player/lazy for optimal code splitting.
 *
 * @example
 * ```tsx
 * import { ReactPlayer } from 'utils/loadablePlayers';
 *
 * <ReactPlayer url="https://youtube.com/..." playing controls />
 * ```
 */
export const ReactPlayer = loadable(() => import('react-player/lazy'), {
  fallback: <VideoPlaceholder />,
});

/**
 * SSR-safe YouTube Player (optimized for YouTube videos)
 *
 * Only loads the YouTube player, smaller bundle than ReactPlayer.
 * Use when you know the video is from YouTube.
 *
 * @example
 * ```tsx
 * import { YouTubePlayer } from 'utils/loadablePlayers';
 *
 * <YouTubePlayer url="https://youtube.com/..." playing controls />
 * ```
 */
export const YouTubePlayer = loadable(() => import('react-player/youtube'), {
  fallback: <VideoPlaceholder />,
});

/**
 * SSR-safe File Player (for local video files)
 *
 * Plays video/audio files using HTML5 media elements.
 *
 * @example
 * ```tsx
 * import { FilePlayer } from 'utils/loadablePlayers';
 *
 * <FilePlayer url="/videos/demo.mp4" playing loop muted />
 * ```
 */
export const FilePlayer = loadable(() => import('react-player/file'), {
  fallback: <VideoPlaceholder />,
});
